import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Alert,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Biotech as BiotechIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import {
  idexxService,
  CreateLabRequestPayload,
  LabRequest,
  IdexxRefTest,
  IdexxIvlsDevice,
  IdexxLabPanel,
} from '../../services/idexxService';
import IdexxOrderingIframe from './IdexxOrderingIframe';

/**
 * NewLabRequestDialog — analog of NewImagingRequestDialog but for IDEXX
 * lab orders. Mounted by the parent so the +New order menu and the
 * per-row Edit button on DRAFT/CREATED rows share a single dialog
 * instance. `editTarget` flips it into edit mode (prefilled).
 *
 * Workflow:
 *   1. Pick modality: REFLAB (sample to reference lab) or INHOUSE (IVLS analyzer)
 *   2. Pick tests from the cached IDEXX catalog (filtered to equine-applicable)
 *   3. For INHOUSE — pick the destination IVLS unit
 *   4. Optional vet/technician/notes/specimen-date
 *   5. Save as Draft (local only) OR Save & Submit (POST to IDEXX, open uiUrl)
 */

interface NewLabRequestDialogProps {
  open: boolean;
  horseId: string;
  editTarget?: LabRequest | null;
  onClose: () => void;
  onSuccess: (request: LabRequest) => void;
}

interface FormState {
  modality: 'REFLAB' | 'INHOUSE';
  veterinarian: string;
  technician: string;
  specimenCollectionDate: string; // YYYY-MM-DD
  notes: string;
  testCodes: string[];
  ivlsSerialNumber: string;
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function emptyForm(): FormState {
  return {
    modality: 'REFLAB',
    veterinarian: '',
    technician: '',
    specimenCollectionDate: todayYmd(),
    notes: '',
    testCodes: [],
    ivlsSerialNumber: '',
  };
}

const NewLabRequestDialog: React.FC<NewLabRequestDialogProps> = ({
  open,
  horseId,
  editTarget,
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [tests, setTests] = useState<IdexxRefTest[]>([]);
  const [ivlsDevices, setIvlsDevices] = useState<IdexxIvlsDevice[]>([]);
  const [panels, setPanels] = useState<IdexxLabPanel[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After a successful Save & Submit we keep the row + uiUrl around so the
  // embedded iframe modal can drive the finalize step. `iframeRow` doubles
  // as the "submitted" flag — non-null means the iframe modal is open.
  const [iframeRow, setIframeRow] = useState<LabRequest | null>(null);

  const isEdit = !!editTarget;

  // Reset / hydrate on open.
  useEffect(() => {
    if (!open) return;
    if (editTarget) {
      setForm({
        modality: (editTarget.modality as 'REFLAB' | 'INHOUSE') || 'REFLAB',
        veterinarian: editTarget.veterinarian || '',
        technician: editTarget.technician || '',
        specimenCollectionDate:
          editTarget.specimenCollectionDate?.slice(0, 10) || todayYmd(),
        notes: editTarget.notes || '',
        testCodes: editTarget.testCodes || [],
        ivlsSerialNumber: editTarget.ivlsSerialNumber || '',
      });
    } else {
      setForm(emptyForm());
    }
    setError(null);
  }, [open, editTarget]);

  // Load catalog when the dialog opens (cheap — these are cached endpoints).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCatalog(true);
    Promise.all([
      idexxService.listTests({ equineOnly: true }),
      idexxService.listIvlsDevices(),
      idexxService.listPanels(),
    ])
      .then(([t, d, p]) => {
        if (cancelled) return;
        setTests(t);
        setIvlsDevices(d);
        setPanels(p);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err.response?.data?.message ||
            'Failed to load IDEXX catalog. Try Refresh Catalog in Settings → Integrations.',
        );
      })
      .finally(() => !cancelled && setLoadingCatalog(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  const modalityPanels = useMemo(
    () => panels.filter((p) => p.modality === form.modality),
    [panels, form.modality],
  );

  // Per IDEXX (Chris): the test list returns BOTH in-house and ref-lab tests;
  // in-house tests are the `IHD_`-prefixed codes. Scope the picker to the
  // chosen modality so a single order is never a mix of in-house + ref-lab.
  // Also fold in any tests referenced by panels for this modality — IDEXX
  // flags only ONE in-house test "equine", so without this the panel building
  // blocks (CBC, SDMA, Lyte…) wouldn't be individually pickable/resolvable.
  const modalityTests = useMemo(() => {
    const base = tests.filter((t) =>
      form.modality === 'INHOUSE'
        ? t.code.startsWith('IHD_')
        : !t.code.startsWith('IHD_'),
    );
    const seen = new Set(base.map((t) => t.code));
    const extra: IdexxRefTest[] = [];
    for (const p of modalityPanels) {
      for (const t of p.tests) {
        if (!seen.has(t.code)) {
          seen.add(t.code);
          extra.push({ code: t.code, name: t.name } as IdexxRefTest);
        }
      }
    }
    return [...base, ...extra];
  }, [tests, modalityPanels, form.modality]);

  const selectedTests = useMemo(
    () => modalityTests.filter((t) => form.testCodes.includes(t.code)),
    [modalityTests, form.testCodes],
  );

  const applyPanel = (panel: IdexxLabPanel) => {
    setForm((f) => ({
      ...f,
      testCodes: Array.from(new Set([...f.testCodes, ...panel.testCodes])),
    }));
  };

  const totalPrice = useMemo(() => {
    return selectedTests.reduce((sum, t) => {
      const p = parseFloat(t.listPrice ?? '0');
      return Number.isFinite(p) ? sum + p : sum;
    }, 0);
  }, [selectedTests]);

  const canSave =
    form.veterinarian.trim().length > 0 &&
    (form.modality !== 'INHOUSE' || form.ivlsSerialNumber.length > 0);

  const buildPayload = (): CreateLabRequestPayload => ({
    horseId,
    modality: form.modality,
    veterinarian: form.veterinarian.trim(),
    technician: form.technician.trim() || undefined,
    specimenCollectionDate: form.specimenCollectionDate || undefined,
    notes: form.notes.trim() || undefined,
    testCodes: form.testCodes,
    ivlsSerialNumber:
      form.modality === 'INHOUSE' ? form.ivlsSerialNumber : undefined,
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      let result: LabRequest;
      if (isEdit && editTarget) {
        result = await idexxService.updateLabRequest(editTarget.id, buildPayload());
      } else {
        result = await idexxService.createLabRequest(buildPayload());
      }
      onSuccess(result);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save lab request.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitToIdexx = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Persist first (create or update), then submit.
      let row: LabRequest = isEdit && editTarget
        ? await idexxService.updateLabRequest(editTarget.id, buildPayload())
        : await idexxService.createLabRequest(buildPayload());
      row = await idexxService.submitLabRequest(row.id);
      if (row.uiUrl) {
        // Open the VC+ Ordering iframe so the user can finalize tests +
        // specimen details inline. We DON'T call onSuccess yet — that fires
        // when the iframe modal closes (either via ui/done postMessage or
        // the user clicking "I'm done — refresh order").
        setIframeRow(row);
      } else {
        // No uiUrl came back — order was auto-finalized server-side. Close.
        onSuccess(row);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit order to IDEXX.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIframeDone = async () => {
    if (!iframeRow) return;
    try {
      const refreshed = await idexxService.refreshLabRequest(iframeRow.id);
      setIframeRow(null);
      // Reference-lab orders MUST ship the requisition form with the sample,
      // so open it for printing as soon as the order is finalized. Non-fatal
      // if it isn't ready — the card's Print button can reprint it anytime.
      if (refreshed.modality === 'REFLAB') {
        try {
          const { url } = await idexxService.getReqFormUrl(refreshed.id);
          if (url) window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
          /* swallow — reprint available from the lab request card */
        }
      }
      onSuccess(refreshed);
    } catch (err: any) {
      // Don't lose the iframe context if refresh fails — let user retry.
      setError(err.response?.data?.message || 'Failed to refresh order from IDEXX.');
    }
  };

  const handleIframeClose = () => {
    // User closed without confirming. The row is still SUBMITTED on IDEXX's
    // side — surface it in the pending strip via onSuccess so they can
    // refresh / re-open in VC+ later.
    if (iframeRow) {
      setIframeRow(null);
      onSuccess(iframeRow);
    }
  };

  const formatPrice = (raw?: string | null) => {
    if (!raw) return '';
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return '';
    return `$${n.toFixed(2)}`;
  };

  // While the iframe modal is up, render only it — the form dialog hides
  // behind so the user has a clean handoff to VC+.
  if (iframeRow) {
    return (
      <IdexxOrderingIframe
        open={!!iframeRow}
        uiUrl={iframeRow.uiUrl ?? null}
        onDone={handleIframeDone}
        onClose={handleIframeClose}
      />
    );
  }

  return (
    <Dialog open={open} onClose={saving || submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BiotechIcon color="primary" />
          {isEdit ? 'Edit IDEXX Lab Request' : 'New IDEXX Lab Request'}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Order modality
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={form.modality}
              onChange={(_, v) => v && setForm({ ...form, modality: v as FormState['modality'], testCodes: [] })}
              size="small"
              color="primary"
            >
              <ToggleButton value="REFLAB">
                <ScienceIcon fontSize="small" sx={{ mr: 1 }} />
                Reference Lab (sample mailed)
              </ToggleButton>
              <ToggleButton value="INHOUSE">
                <BiotechIcon fontSize="small" sx={{ mr: 1 }} />
                In-House (IVLS)
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {form.modality === 'INHOUSE' && (
            <TextField
              select
              label="IVLS unit"
              value={form.ivlsSerialNumber}
              onChange={(e) => setForm({ ...form, ivlsSerialNumber: e.target.value })}
              fullWidth
              required
              helperText={
                ivlsDevices.length === 0
                  ? 'No IVLS units registered for this practice. Refresh Catalog in Settings to re-check.'
                  : 'Sample will be processed on this analyzer.'
              }
            >
              {ivlsDevices.map((d) => (
                <MenuItem key={d.serialNumber} value={d.serialNumber}>
                  {d.displayName} — {d.serialNumber}
                </MenuItem>
              ))}
            </TextField>
          )}

          {modalityPanels.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Quick panels
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mb={0.5}>
                {modalityPanels.map((p) => (
                  <Chip
                    key={p.id}
                    label={p.name}
                    icon={<BiotechIcon />}
                    onClick={() => applyPanel(p)}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Click a panel to add its tests. Manage panels in Settings → Integrations → IDEXX.
              </Typography>
            </Box>
          )}

          <Autocomplete
            multiple
            options={modalityTests}
            value={selectedTests}
            onChange={(_, value) => setForm({ ...form, testCodes: value.map((t) => t.code) })}
            getOptionLabel={(t) => `${t.name}${t.listPrice ? ` — ${formatPrice(t.listPrice)}` : ''}`}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            loading={loadingCatalog}
            filterSelectedOptions
            renderOption={(props, option) => (
              <li {...props}>
                <Box display="flex" flexDirection="column" width="100%">
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={500}>
                      {option.name}
                    </Typography>
                    {option.listPrice && (
                      <Typography variant="caption" color="text.secondary">
                        {formatPrice(option.listPrice)} {option.currencyCode}
                      </Typography>
                    )}
                  </Box>
                  {(option.turnaround || option.specimen) && (
                    <Typography variant="caption" color="text.secondary">
                      {option.turnaround && `Turnaround: ${option.turnaround}`}
                      {option.turnaround && option.specimen && ' · '}
                      {option.specimen}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tests"
                placeholder={form.testCodes.length === 0 ? 'Pick tests (or leave empty to choose in VC+)' : ''}
                helperText={
                  loadingCatalog
                    ? 'Loading catalog…'
                    : `${modalityTests.length} equine-applicable ${form.modality === 'INHOUSE' ? 'in-house (IVLS)' : 'reference-lab'} tests available. Leave empty to choose in the VC+ UI on submit.`
                }
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={option.code}
                    label={`${option.name}${option.listPrice ? ` · ${formatPrice(option.listPrice)}` : ''}`}
                    size="small"
                    {...tagProps}
                  />
                );
              })
            }
          />

          {totalPrice > 0 && (
            <Typography variant="caption" color="text.secondary">
              Estimated total: ${totalPrice.toFixed(2)} (list price — clinic-specific pricing may differ)
            </Typography>
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="Veterinarian"
              value={form.veterinarian}
              onChange={(e) => setForm({ ...form, veterinarian: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Technician (optional)"
              value={form.technician}
              onChange={(e) => setForm({ ...form, technician: e.target.value })}
              fullWidth
            />
          </Stack>
          <TextField
            label="Specimen collection date"
            type="date"
            value={form.specimenCollectionDate}
            onChange={(e) => setForm({ ...form, specimenCollectionDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            helperText="Special instructions for the lab. Up to 1000 chars (US/UK/AU) or 255 chars elsewhere."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || submitting}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={handleSaveDraft}
          disabled={!canSave || saving || submitting}
        >
          {saving ? <CircularProgress size={20} /> : 'Save as Draft'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitToIdexx}
          disabled={!canSave || saving || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : 'Save & Submit to IDEXX'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewLabRequestDialog;
